import { useMap } from '@vis.gl/react-google-maps';
import { AgenturModel } from 'app/models/agentur.model';
import { BBox } from 'geojson';
import { useMemo, useReducer, useEffect, useCallback, useState } from 'react';
import Supercluster from 'supercluster';

const superclusterOptions = {
	radius: 100,
	maxZoom: 16,
	minPoints: 2,
};
export function useSupercluster(agts: AgenturModel[]) {
	// convert items to GeoJSON format
	const geojson = useMemo(() => {
		const filteredAgts = agts.filter((agt) => agt.lat && agt.lng);
		return {
			type: 'FeatureCollection' as const,
			features: filteredAgts.map((agt) => ({
				type: 'Feature' as const,
				id: agt.agtId,
				properties: {
					agtId: agt.agtId,
					name: agt.name,
				},
				geometry: {
					type: 'Point' as const,
					coordinates: [agt.lng!, agt.lat!],
				},
			})),
		};
	}, [agts]);

	// create a new clusterer every time geojson.features changes
	const clusterer = useMemo(() => {
		const c = new Supercluster(superclusterOptions);
		c.load(geojson.features);
		return c;
	}, [geojson]);

	// get bounding-box and zoomlevel from the map
	const { bbox, zoom } = useMapViewport({ padding: 100 });

	// retrieve the clusters within the current viewport
	const clusters = useMemo(() => {
		if (!clusterer) return [];

		return clusterer.getClusters(bbox, zoom);
	}, [clusterer, bbox, zoom]);

	// create callbacks to expose supercluster functionality outside of this hook
	const getChildren = useCallback((clusterId: number) => clusterer.getChildren(clusterId), [clusterer]);
	const getLeaves = useCallback((clusterId: number) => clusterer.getLeaves(clusterId, Infinity), [clusterer]);
	const getClusterExpansionZoom = useCallback((clusterId: number) => clusterer.getClusterExpansionZoom(clusterId), [clusterer]);

	return {
		clusters,
		getChildren,
		getLeaves,
		getClusterExpansionZoom,
	};
}

interface IMapViewportOptions {
	padding?: number; // padding in degrees
}
export function useMapViewport({ padding = 0 }: IMapViewportOptions = {}) {
	const map = useMap();
	const [bbox, setBbox] = useState<BBox>([-180, -90, 180, 90]);
	const [zoom, setZoom] = useState(0);

	// observe the map to get current bounds
	useEffect(() => {
		if (!map) return;

		const listener = map.addListener('bounds_changed', () => {
			const bounds = map.getBounds();
			const zoom = map.getZoom();
			const projection = map.getProjection();

			if (!bounds || !zoom || !projection) return;

			const sw = bounds.getSouthWest();
			const ne = bounds.getNorthEast();

			const paddingDegrees = degreesPerPixel(zoom) * padding;

			const n = Math.min(90, ne.lat() + paddingDegrees);
			const s = Math.max(-90, sw.lat() - paddingDegrees);

			const w = sw.lng() - paddingDegrees;
			const e = ne.lng() + paddingDegrees;

			setBbox([w, s, e, n]);
			setZoom(zoom);
		});

		return () => listener.remove();
	}, [map, padding]);

	return { bbox, zoom };
}

function degreesPerPixel(zoomLevel: number) {
	// 360° divided by the number of pixels at the zoom-level
	return 360 / (Math.pow(2, zoomLevel) * 256);
}
